import {
  Drawer,
  Form,
  Input,
  Switch,
  Button,
  Space,
  Skeleton,
  Descriptions,
  Typography,
  Select,
} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { XAICallModel } from "@lib/types/xAI";
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGetXAICallModel } from "@lib/api/xAI";
import dayjs from "dayjs";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import styled from "styled-components";
import { UseMutationProps } from "@lib/types/Api";
import { convert2label } from "@lib/helpers/StringHelper";
import { GenderEnum } from "@lib/types/xAI";
import {
  IsDisabledTag,
  LanguageLabel,
} from "@lib/components/MiniComponents/Others";
import { LANGUAGE_CODE } from "@lib/types/Country";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  open: boolean;
  onClose: () => void;
  item: XAICallModel | null;
  createMutation: UseMutationProps<XAICallModel>;
  updateMutation: UseMutationProps<Partial<XAICallModel>>;
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 9 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

export const XAICallModelDrawer = (props: Props) => {
  const { open, onClose, item, createMutation, updateMutation } = props;
  const { messages, formatMessage } = useIntl();
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);

  const modelQuery = useQuery({
    queryFn: () => apiGetXAICallModel(item?.id || ""),
    queryKey: ["xai-call-model", item?.id],
    enabled: !!item?.id && open,
  });

  const modelData = modelQuery?.data?.data || item;
  const isLoading = modelQuery?.isLoading || modelQuery?.isFetching;

  useEffect(() => {
    if (open) {
      if (item) {
        setIsEditMode(false);
        if (modelData) {
          form?.setFieldsValue(modelData);
        }
      } else {
        setIsEditMode(true);
        form?.resetFields();
        form?.setFieldsValue({
          isDisabled: false,
        });
      }
    }
  }, [open, item, modelData, form]);

  const handleClose = useCallback(() => {
    setIsEditMode(false);
    form?.resetFields();
    onClose();
  }, [onClose, form]);

  const handleSubmit = useCallback(
    async (values: Partial<XAICallModel>) => {
      if (item?.id) {
        try {
          const response = await updateMutation.mutateAsync({
            id: item.id,
            ...values,
          });
          setIsEditMode(false);
          const refetchedData = await modelQuery?.refetch();
          // Update form with latest data
          if (refetchedData?.data?.data) {
            form?.setFieldsValue(refetchedData.data.data);
          } else if (response?.data) {
            form?.setFieldsValue(response.data);
          }
        } catch (error) {
          // Error handling is done by mutation
        }
      } else {
        createMutation.mutate(values as XAICallModel);
      }
    },
    [item, createMutation, updateMutation, modelQuery, form],
  );

  const handleReset = useCallback(() => {
    if (item) {
      form?.setFieldsValue(modelData);
    } else {
      form?.resetFields();
      form?.setFieldsValue({
        isDisabled: false,
      });
    }
  }, [form, item, modelData]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <StyledDrawer
      title={
        <FormattedMessage
          id={
            isEditMode
              ? item
                ? "common.editTitle"
                : "common.createTitle"
              : "common.previewTitle"
          }
          values={{
            title: messages["sidebar.xaiCallModels"] as string,
          }}
        />
      }
      onClose={handleClose}
      open={open}
      width={600}
      id="xai-call-model-drawer"
      extra={
        !isEditMode &&
        item && (
          <Button
            type="primary"
            onClick={() => setIsEditMode(true)}
            disabled={isLoading}
          >
            <FormattedMessage id="common.edit" />
          </Button>
        )
      }
    >
      {isEditMode ? (
        <Form<XAICallModel>
          {...layout}
          form={form}
          name="xai-call-model-form"
          onFinish={handleSubmit}
          style={{ maxWidth: 600 }}
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label={messages["common.name"] as string}
            rules={[
              {
                required: true,
                message: formatMessage(
                  { id: "validation.required" },
                  { field: messages["common.name"] as string },
                ),
              },
            ]}
          >
            <Input
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.name"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="aiModelId"
            label={messages["common.aiModelId"] as string}
            rules={[
              {
                required: true,
                message: formatMessage(
                  { id: "validation.required" },
                  { field: messages["common.aiModelId"] as string },
                ),
              },
            ]}
          >
            <Input
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.aiModelId"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="language"
            label={messages["common.language"] as string}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="text"
              options={Object.entries(LANGUAGE_CODE).map(([key, value]) => ({
                label: convert2label(key),
                text: convert2label(key),
                value: value,
              }))}
              placeholder={formatMessage(
                {
                  id: "helper.pleaseSelectToX",
                },
                { x: messages["common.language"] as string },
              )}
            />
          </Form.Item>

          <Form.Item name="gender" label={messages["common.gender"] as string}>
            <Select
              allowClear
              showSearch
              optionFilterProp="text"
              options={Object.entries(GenderEnum).map(([key, value]) => ({
                label: convert2label(key),
                text: convert2label(key),
                value: value,
              }))}
              placeholder={formatMessage(
                {
                  id: "helper.pleaseSelectToX",
                },
                { x: messages["common.gender"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="personality"
            label={messages["common.personality"] as string}
          >
            <Input
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.personality"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="purpose"
            label={messages["common.purpose"] as string}
          >
            <Input
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.purpose"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={messages["common.description"] as string}
          >
            <Input.TextArea
              rows={4}
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.description"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="isDisabled"
            label={messages["common.isDisabled"] as string}
            valuePropName="checked"
          >
            <Switch
              checkedChildren={messages["common.enabled"] as string}
              unCheckedChildren={messages["common.disabled"] as string}
            />
          </Form.Item>

          <StyledFlex style={{ justifyContent: "end" }}>
            <Space>
              <Button
                htmlType="button"
                onClick={() => {
                  if (item) {
                    setIsEditMode(false);
                    handleReset();
                  } else {
                    handleClose();
                  }
                }}
                disabled={isPending}
              >
                {messages["common.cancel"] as string}
              </Button>
              <Button
                htmlType="button"
                onClick={handleReset}
                disabled={isPending}
              >
                {messages["common.reset"] as string}
              </Button>
              <Button type="primary" htmlType="submit" loading={isPending}>
                {item?.id
                  ? (messages["common.update"] as string)
                  : (messages["common.create"] as string)}
              </Button>
            </Space>
          </StyledFlex>
        </Form>
      ) : isLoading ? (
        <SkeletonContent />
      ) : (
        <StyledDescriptions bordered column={1}>
          <Descriptions.Item label="ID">
            <Typography.Text copyable={{ text: modelData?.id }}>
              {modelData?.id || "-"}
            </Typography.Text>
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.name" />}>
            {modelData?.name || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.aiModelId" />}>
            {modelData?.aiModelId || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.language" />}>
            <LanguageLabel languageCode={modelData?.language} fallback="-" />
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.gender" />}>
            {modelData?.gender
              ? convert2label(
                  Object.keys(GenderEnum).find(
                    (key) =>
                      GenderEnum[key as keyof typeof GenderEnum] ===
                      modelData.gender,
                  ) || "",
                )
              : "-"}
          </Descriptions.Item>

          <Descriptions.Item
            label={<FormattedMessage id="common.personality" />}
          >
            {modelData?.personality || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.purpose" />}>
            {modelData?.purpose || "-"}
          </Descriptions.Item>

          <Descriptions.Item
            label={<FormattedMessage id="common.description" />}
          >
            {modelData?.description || "-"}
          </Descriptions.Item>

          <Descriptions.Item
            label={<FormattedMessage id="common.isDisabled" />}
          >
            <IsDisabledTag isDisabled={modelData?.isDisabled} />
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.createdAt" />}>
            {modelData?.createdAt
              ? dayjs(modelData.createdAt).format("DD/MM/YYYY HH:mm:ss")
              : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.createdBy" />}>
            {modelData?.createdBy || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.updatedAt" />}>
            {modelData?.updatedAt
              ? dayjs(modelData.updatedAt).format("DD/MM/YYYY HH:mm:ss")
              : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.updatedBy" />}>
            {modelData?.updatedBy || "-"}
          </Descriptions.Item>
        </StyledDescriptions>
      )}
    </StyledDrawer>
  );
};
